import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import traiteurImg from "../../images/21.png";
import djImg from "../../images/22.png";
import decorationImg from "../../images/23.png";
import animationImg from "../../images/24.png";
import locationImg from "../../images/25.png";
import photographyImg from "../../images/26.png";

// Default card data
const defaultCardData = [
  {
    id: 1,
    title: "Traiteurs",
    image: traiteurImg,
    to: "/prestations",
    height: "245px",
    colSpan: { lg: 3, md: 1 },
  },
  {
    id: 2,
    title: "Décoration",
    image: decorationImg,
    to: "/prestations",
    height: "245px",
    colSpan: { lg: 3, md: 1 },
  },
  {
    id: 3,
    title: "Photographie et tournage",
    image: photographyImg,
    to: "/prestations",
    height: "516px",
    colSpan: { lg: 4, md: 1 },
  },
  {
    id: 4,
    title: "Musique et DJ",
    image: djImg,
    to: "/prestations",
    height: "245px",
    colSpan: { lg: 5, md: 2 },
  },
  {
    id: 5,
    title: "Animation",
    image: animationImg,
    to: "/prestations",
    height: "245px",
    colSpan: { lg: 5, md: 1 },
  },
  {
    id: 6,
    title: "Location de matériel",
    image: locationImg,
    to: "/prestations",
    height: "245px",
    colSpan: { lg: 7, md: 1 },
  },
];

// Animation variants for cards
const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.5, ease: "easeOut" },
  }),
  hover: { scale: 1.03, boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)" },
};

// Define prop types
interface CardData {
  id: number;
  title: string;
  image: string;
  to: string;
  height: string;
  colSpan: { lg: number; md: number };
}

interface CallToActionImageProps {
  title?: string;
  cardData?: CardData[];
  className?: string;
  controls?: any;
}

// Reusable CallToActionImage Component
function PrestaCallToActionImage({
  title = "Les prestataires événementiels autour de Paris",
  cardData = defaultCardData,
  className = " dark:bg-neutral-800 dark:from-neutral-800",
  controls,
}: CallToActionImageProps) {
  return (
    <div className={className}>
      <div className="container px-4 py-12 mx-auto mt-20">
        {/* Animated Title */}
        <div className="mb-12 text-4xl font-bold text-center text-gray-800 md:text-5xl dark:text-white">
          <h2>{title}</h2>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12">
          {/* First Column */}
          <div className="space-y-6 lg:col-span-3 md:col-span-1">
            {cardData.slice(0, 2).map((card, index) => (
              <Card key={card.id} card={card} index={index} />
            ))}
          </div>

          {/* Second Column */}
          <div className="lg:col-span-4 md:col-span-1">
            <Card card={cardData[2]} index={2} />
          </div>

          {/* Third Column */}
          <div className="lg:col-span-5 md:col-span-2">
            <Card card={cardData[3]} index={3} className="mb-6" />
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12">
              <div className="lg:col-span-5 md:col-span-1">
                <Card card={cardData[4]} index={4} />
              </div>
              <div className="lg:col-span-7 md:col-span-1">
                <Card card={cardData[5]} index={5} />
              </div>
            </div>
          </div>
        </div>

        {/* RTL Support */}
        <style>{`
          [dir='rtl'] .discount-single-item .discount {
            left: auto;
            right: 1.25rem;
          }
        `}</style>
      </div>
    </div>
  );
}

// Reusable Card Component
function Card({ card, index, className = "" }: { card: CardData; index: number; className?: string }) {
  return (
    <AnimatePresence>
      <motion.div
        className={`relative block rounded-2xl overflow-hidden ${className}`}
        style={{ height: card.height }}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileHover="hover"
        custom={index}
      >
        <Link to={card.to} className="block w-full h-full">
          {/* Background Image */}
          <div className="absolute inset-0 bg-center bg-cover" style={{ backgroundImage: `url(${card.image})` }} />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[rgba(17,24,39,0.9)] to-[rgba(17,24,39,0)]" />
          {/* Content */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <motion.h3
              className="text-lg font-semibold text-white md:text-xl"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
            >
              {card.title}
            </motion.h3>
          </div>
        </Link>
      </motion.div>
    </AnimatePresence>
  );
}

export default PrestaCallToActionImage;
